import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { vehicleBreakDownTableHeaders } from 'src/utils/tableHeaders';
import {
  camelCaseString,
  getFormattedDate,
  getFuelPrice,
  getSelectedSiteFilterValue
} from 'src/utils/getInitials';
import { getVehicleBreakDownList } from 'src/redux/vehicleBreakDownSlice';
import EnhancedTableCell from 'src/common/EnhancedTableCell';
import { TransactionPagination } from 'src/components/transactions/TransactionListTable';
import BreakdownApprovalForm from './BreakdownApprovalForm';

const BreakdownList = ({
  refreshData, searchText, searchFilters = {}, siteFilter, ...rest
}) => {
  const dispatch = useDispatch();
  const { vehicleBreakDownList, loading, totalCount } = useSelector((state) => state.vehicleBreakDown);
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [openReviewModal, setReviewModal] = useState(false);
  const [selectedBreakdown, setSelectedBreakdown] = useState({});
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(getVehicleBreakDownList({
      limit, pageNo: 1, params: { ...searchFilters, vehicle__registration_num: searchFilters.vehicle__registration_num || searchText, site: selectedSite }
    }));
  }, [dispatch, limit, refreshData, searchText, searchFilters, selectedSite]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(0);
    setLimit(newLimit);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getVehicleBreakDownList({
      limit, pageNo: newPage + 1, params: { ...searchFilters, vehicle__registration_num: searchFilters.vehicle__registration_num || searchText, site: selectedSite }
    }));
  };

  const reviewBreakdown = (breakdown) => {
    setSelectedBreakdown(breakdown);
    setReviewModal(true);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getChipColor = (status) => {
    let chipColor = 'primary';
    if (status === 'completed') {
      chipColor = 'success';
    } else if (status === 'rejected') {
      chipColor = 'error';
    }
    return chipColor;
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Card {...rest}>
          <BreakdownApprovalForm
            open={openReviewModal}
            initialValues={selectedBreakdown}
            handleClose={() => setReviewModal(false)}
          />
          <PerfectScrollbar>
            <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 } }}>
              <Table sx={{ tableLayout: 'auto', minWidth: 1590 }}>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  headCells={vehicleBreakDownTableHeaders}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {vehicleBreakDownList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={vehicleBreakDownTableHeaders.length} align="center">
                        <Box sx={{ py: 5 }}>
                          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                            No vehicle breakdowns found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : vehicleBreakDownList
                    .slice()
                    .sort(getComparator(order, orderBy))
                    // .slice(page * limit, page * limit + limit)
                    .map((vehicle) => (
                      <TableRow hover key={vehicle.id}>
                        <TableCell align="center" sx={{ width: 80, minWidth: 80 }}>
                          <Box
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                              justifyContent: 'center',
                              width: '100%'
                            }}
                          >
                            <Typography color="textPrimary" variant="body1">
                              {vehicle.id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 170 }}>{vehicle.vehicle_number}</TableCell>
                        <TableCell align="center" sx={{ minWidth: 150 }}>{vehicle.type}</TableCell>
                        <TableCell align="center" sx={{ minWidth: 180 }}>{vehicle.location}</TableCell>
                        <TableCell align="center" sx={{ minWidth: 190 }}>{getFormattedDate(vehicle.start_time)}</TableCell>
                        <TableCell align="center" sx={{ minWidth: 120 }}>{getFuelPrice(vehicle.expense)}</TableCell>
                        <TableCell align="center" sx={{ minWidth: 150 }}>{vehicle.duration}</TableCell>
                        <EnhancedTableCell title={vehicle.description} align="center" />
                        <EnhancedTableCell title={vehicle.remarks} align="center" />
                        <TableCell align="center" sx={{ minWidth: 130 }}>
                          <Chip
                            label={camelCaseString(vehicle.status)}
                            color={getChipColor(vehicle.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: 120,
                            minWidth: 120,
                            position: 'sticky',
                            right: 0,
                            zIndex: 2,
                            backgroundColor: '#FFFFFF',
                            borderLeft: '1px solid #CBD5E1',
                            boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.12)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              left: -9,
                              width: 9,
                              pointerEvents: 'none',
                              background: 'linear-gradient(90deg, rgba(15, 23, 42, 0), rgba(15, 23, 42, 0.12))'
                            }
                          }}
                        >
                          <Button
                            className="vehicle-breakdown-review-button"
                            variant="contained"
                            onClick={() => reviewBreakdown(vehicle)}
                            disabled={vehicle.status !== 'pending'}
                            size="small"
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </PerfectScrollbar>
          <TransactionPagination
            count={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handleLimitChange}
            page={page}
            pageSize={limit}
          />
        </Card>
      )}
    </>
  );
};

BreakdownList.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  searchFilters: PropTypes.object,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default BreakdownList;
